import { useState, useEffect, Fragment } from 'react';
import {
  Autocomplete,
  Modal,
  Box,
  Stack,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Button,
  styled,
  InputLabel,
  CircularProgress,
  List,
  FormHelperText,
} from '@mui/material';
import Dropzone from '../../../../components/Dropzone/Dropzone';
import { GetProducerDownlines } from '../../../../api/admin';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  GetIMORequestTypes,
  GetAgentRequestTypes,
  SubmitChangeRequest,
} from '../../../../api/MyIMO';
import { useUser } from '../../../../context/UserContext';
import SubmitContractRequestFeedback from '../feedback/SubmitContractRequestFeedback';

const ModalContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: 500,
  minHeight: 400,
  maxHeight: 800,
  padding: '1rem 2rem',
  overflowY: 'scroll',
  borderRadius: 2,
  background: theme.palette.background.paper,
}));

const NewChangeRequestModal = ({
  showModal,
  toggleModal,
  producerID,
  refetchRequests,
  IMOSubmission,
}) => {
  const user = useUser();

  const formData = new FormData();
  const [fetchDownlines, setFetchDownlines] = useState(false);
  const initFormErrors = {
    npn: '',
    requestInformation: '',
    producerID: '',
    requestType: '',
    files: '',
  };
  const initFormState = {
    requestInfo: {
      producerID: IMOSubmission ? '' : producerID,
      submitterNPN: user.npnID,
      requestType: null,
      dataObject: {
        nationalProducerNumber: '',
        requestInformation: '',
        iProvide: false,
        iQumulate: false,
      },
    },
    isIMORequest: false,
    documentsList: [],
  };
  const [agentPortalIRSubmissionRequest, setAgentPortalIRSubmissionRequest] =
    useState(initFormState);
  const [requestCommonName, setRequestCommonName] = useState('');
  const [formErrors, setFormErrors] = useState(initFormErrors);

  const getProducerDownlines = useQuery({
    queryKey: ['getProducerDownlinesMyIMO', producerID],
    queryFn: () => GetProducerDownlines({ ProducerID: producerID })(),
    enabled: !!fetchDownlines && IMOSubmission === true,
    select: (data) => {
      return data.filter((item) => item.down_ContractStatusDesc === 'Active');
    },
  });

  // get different request types based on if it's a my profile or my IMO submission
  let getRequestTypes;
  if (IMOSubmission) {
    getRequestTypes = useQuery({
      queryKey: ['getIMORequestTypes'],
      queryFn: () => GetIMORequestTypes()(),
    });
  } else {
    getRequestTypes = useQuery({
      queryKey: ['getAgentRequestTypes'],
      queryFn: () => GetAgentRequestTypes()(),
    });
  }

  const {
    mutate,
    data: mutationResult,
    isSuccess: mutationSuccess,
    isError: mutationError,
    isPending: mutationIsLoading,
    reset: resetRequestForm,
  } = useMutation({
    mutationFn: (data) => SubmitChangeRequest(data)(),
  });

  const requiresFiles =
    requestCommonName === 'Banking Update' ||
    requestCommonName === 'Commission / Profile Update' ||
    requestCommonName === 'Training Update' ||
    requestCommonName === 'Hierarchy Update';

  const handleFormReset = () => {
    setAgentPortalIRSubmissionRequest(initFormState);
    setRequestCommonName('');
    resetRequestForm();
  };

  const handleFileUpload = (files) => {
    setAgentPortalIRSubmissionRequest((prev) => ({
      ...prev,
      documentsList: files,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      files: '',
    }));
  };

  const handleRequestType = (e) => {
    const { name, value } = e.target;
    setAgentPortalIRSubmissionRequest((prev) => ({
      ...prev,
      requestInfo: {
        ...prev.requestInfo,
        [name]: value.id,
      },
    }));
    setRequestCommonName(value.requestDisplayName);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const handleInput = (e) => {
    const { name, value } = e.target;

    setAgentPortalIRSubmissionRequest((prev) => ({
      ...prev,
      requestInfo: {
        ...prev.requestInfo,
        dataObject: {
          ...prev.dataObject,
          [name]: value,
        },
      },
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    const { producerID, requestType } = agentPortalIRSubmissionRequest.requestInfo;
    const { documentsList, requestInfo } = agentPortalIRSubmissionRequest;

    if (IMOSubmission) {
      if (producerID.trim() === '') {
        newErrors.producerID = 'Producer ID is required.';
        isValid = false;
      }
    }

    if (!requestType) {
      newErrors.requestType = 'Request type is required.';
      isValid = false;
    }

    if (requiresFiles) {
      if (documentsList.length === 0) {
        newErrors.files = 'At least one file is required';
        isValid = false;
      }
    }

    if (!requiresFiles) {
      if (requestInfo.dataObject.requestInformation.trim() === '') {
        newErrors.requestInformation = 'Request details are required';
        isValid = false;
      }
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      ...newErrors,
    }));

    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formIsValid = validateForm();
    if (formIsValid) {
      const { requestInfo } = agentPortalIRSubmissionRequest;
      // append suffix to lastName if exists
      const lastName = requestInfo.dataObject.suffix
        ? `${requestInfo.dataObject.lastName} ${requestInfo.dataObject.suffix}`
        : requestInfo.dataObject.lastName;

      // flatten the request data for multipart / formdata
      formData.append('RequestInfo.ProducerID', requestInfo.producerID ?? '');
      formData.append('RequestInfo.SubmitterNPN', requestInfo.submitterNPN ?? '');
      formData.append('RequestInfo.RequestType', requestInfo.requestType.id ?? '');
      formData.append('IsIMORequest', IMOSubmission === true ? 'true' : 'false');
      formData.append(
        'RequestInfo.DataObject.RequestInformation',
        requestInfo.dataObject.requestInformation ?? ''
      );
      formData.append('RequestInfo.DataObject.FirstName', requestInfo.dataObject.firstName ?? '');
      formData.append('RequestInfo.DataObject.LastName', lastName ?? '');
      formData.append(
        'RequestInfo.DataObject.NationalProducerNumber',
        requestInfo.dataObject.nationalProducerNumber ?? ''
      );
      formData.append('RequestInfo.DataObject.IProvide', requestInfo.dataObject.iProvide);
      formData.append('RequestInfo.DataObject.IQumulate', requestInfo.dataObject.iQumulate);

      agentPortalIRSubmissionRequest.documentsList.forEach((file) => {
        formData.append('DocumentsList', file, file.name);
      });

      mutate(formData);

      if (mutationSuccess) {
        setAgentPortalIRSubmissionRequest(initFormState);
        setFormErrors(initFormErrors);
      }
    }
  };

  useEffect(() => {
    setFormErrors(initFormErrors);

    setAgentPortalIRSubmissionRequest((prev) => ({
      ...initFormState,
      requestInfo: {
        ...initFormState.requestInfo,
        requestType: prev.requestInfo.requestType,
        dataObject: initFormState.requestInfo.dataObject,
        producerID: IMOSubmission
          ? prev.requestInfo.producerID
          : initFormState.requestInfo.producerID,
      },
    }));
  }, [requestCommonName]);

  const showFeedback = mutationIsLoading || mutationSuccess || mutationError;

  return (
    <Modal
      open={showModal}
      onClose={toggleModal}
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '1rem' }}
      keepMounted={true}
    >
      <ModalContainer>
        {showFeedback ? (
          <SubmitContractRequestFeedback
            mutationIsLoading={mutationIsLoading}
            mutationIsError={mutationError}
            mutationIsSuccess={mutationSuccess}
            mutationResult={mutationResult}
            toggleModal={toggleModal}
            resetRequestForm={handleFormReset}
            retryRequest={handleSubmit}
            refetchRequests={refetchRequests}
          />
        ) : (
          <>
            <form style={{ maxHeight: '800px' }}>
              <Stack gap={2} sx={{ padding: '1rem 0' }}>
                <Typography variant="h5">Change Request</Typography>
                {IMOSubmission && (
                  <FormControl>
                    <FormHelperText sx={{ margin: 0, paddingBottom: '.5rem' }}>
                      Which Producer are you submitting a change request for?
                    </FormHelperText>
                    <Autocomplete
                      sx={{ textTransform: 'capitalize' }}
                      disablePortal
                      options={getProducerDownlines.data || []}
                      getOptionLabel={(option) =>
                        option?.down_ProducerId && option?.down_PrintName
                          ? `${option.down_ProducerId.trim()} ${option.down_PrintName.trim()}`
                          : ''
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.down_ProducerId.trim() === value.down_ProducerId.trim()
                      }
                      onOpen={() => setFetchDownlines(true)}
                      onClose={() => setFetchDownlines(false)}
                      loading={getProducerDownlines.isFetching}
                      value={
                        getProducerDownlines.data?.find(
                          (item) =>
                            item.down_ProducerId.trim() ===
                            agentPortalIRSubmissionRequest.requestInfo.producerID
                        ) || null
                      }
                      onChange={(_, value) => {
                        setAgentPortalIRSubmissionRequest((prev) => ({
                          ...prev,
                          requestInfo: {
                            ...prev.requestInfo,
                            producerID: value ? value.down_ProducerId.trim() : '',
                          },
                        }));
                        setFormErrors((prevErrors) => ({
                          ...prevErrors,
                          producerID: '',
                        }));
                      }}
                      slotProps={{
                        listBox: {
                          sx: {
                            maxHeight: 300,
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          id="producerID"
                          name="producerID"
                          error={!!formErrors.producerID}
                          helperText={formErrors.producerID || ''}
                          {...params}
                          label="Producer"
                          sx={{ textTransform: 'capitalize' }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <Fragment>
                                {getProducerDownlines.isFetching ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </FormControl>
                )}
                <Stack>
                  <FormControl error={!!formErrors.requestType}>
                    <Autocomplete
                      value={agentPortalIRSubmissionRequest.requestInfo.requestType}
                      onChange={(_, value) => {
                        setAgentPortalIRSubmissionRequest((prev) => ({
                          ...prev,
                          requestInfo: {
                            ...prev.requestInfo,
                            requestType: value,
                          },
                        }));
                        setRequestCommonName(value?.requestDisplayName || '');
                        setFormErrors((prevErrors) => ({
                          ...prevErrors,
                          [name]: '',
                        }));
                      }}
                      options={getRequestTypes.data || []}
                      getOptionLabel={(option) => option.requestDisplayName || ''}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      loading={getRequestTypes.isFetching}
                      loadingText={<CircularProgress color="primary" size={20} />}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Request Type"
                          error={!!formErrors.requestType}
                          helperText={formErrors.requestType ? formErrors.requestType : ''}
                        />
                      )}
                      noOptionsText={
                        getRequestTypes.isFetching ? 'Loading...' : 'No request types found'
                      }
                    />
                  </FormControl>
                </Stack>
                <Stack>
                  <FormControl>
                    <TextField
                      onChange={handleInput}
                      error={formErrors.requestInformation ? true : false}
                      helperText={
                        formErrors.requestInformation ? formErrors.requestInformation : ''
                      }
                      id="requestInformation"
                      name="requestInformation"
                      label="Request Details"
                      value={
                        agentPortalIRSubmissionRequest.requestInfo.dataObject.requestInformation
                      }
                      multiline
                      minRows={3}
                      maxRows={5}
                    />
                  </FormControl>
                </Stack>
                <Stack direction={'column'}>
                  <Typography variant="caption" color="text.secondary">
                    Which product is this for? Select all that apply.
                  </Typography>
                  <Stack direction={'row'} gap={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agentPortalIRSubmissionRequest.requestInfo.dataObject.iProvide}
                          onChange={(e) => {
                            setAgentPortalIRSubmissionRequest((prev) => ({
                              ...prev,
                              requestInfo: {
                                ...prev.requestInfo,
                                dataObject: {
                                  ...prev.requestInfo.dataObject,
                                  iProvide: e.target.checked,
                                },
                              },
                            }));
                          }}
                          name="iProvide"
                        />
                      }
                      label="IProvide (OLA)"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agentPortalIRSubmissionRequest.requestInfo.dataObject.iQumulate}
                          onChange={(e) => {
                            setAgentPortalIRSubmissionRequest((prev) => ({
                              ...prev,
                              requestInfo: {
                                ...prev.requestInfo,
                                dataObject: {
                                  ...prev.requestInfo.dataObject,
                                  iQumulate: e.target.checked,
                                },
                              },
                            }));
                          }}
                          name="iQumulate"
                        />
                      }
                      label="IQumulate (MYGA)"
                    />
                  </Stack>
                </Stack>
                <Stack>
                  {requestCommonName === 'Banking Update' && (
                    <>
                      <FormHelperText>
                        Please supply the following in support of your request:
                      </FormHelperText>
                      <List>
                        <FormHelperText>&#8226; Voided Check</FormHelperText>
                        <FormHelperText>&#8226; Premium Payment Information</FormHelperText>
                        <FormHelperText>&#8226; EFT Information</FormHelperText>
                      </List>
                    </>
                  )}
                  {requestCommonName === 'Termination Request' && (
                    <>
                      <FormHelperText>
                        {IMOSubmission
                          ? "Please indicate if the Agent's contract should be terminated or if a specific appointment should be terminated"
                          : 'Please indicate if your contract or a specific appointment should be terminated'}
                      </FormHelperText>
                    </>
                  )}
                  {requestCommonName === 'Commission Update' && (
                    <>
                      <FormHelperText>
                        Please upload a copy of the BLC commissions form
                      </FormHelperText>
                    </>
                  )}
                </Stack>
                {requiresFiles && (
                  <Stack>
                    <FormHelperText>Only PDF files are supported</FormHelperText>
                    <Dropzone
                      onFilesAccepted={handleFileUpload}
                      maxFiles={100}
                      accept={{ 'application/pdf': [] }}
                      dropzoneText="Drag & drop or click to select files."
                      clearFiles={agentPortalIRSubmissionRequest.requestInfo.requestType}
                    ></Dropzone>
                    {formErrors.files && (
                      <InputLabel
                        sx={{
                          margin: '0',
                          fontSize: '.75rem',
                        }}
                        variant="error"
                      >
                        {formErrors.files}
                      </InputLabel>
                    )}
                  </Stack>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '.5rem',
                  }}
                >
                  <Button onClick={handleSubmit} variant="contained">
                    Submit
                  </Button>
                </Box>
              </Stack>
            </form>
          </>
        )}
      </ModalContainer>
    </Modal>
  );
};

export default NewChangeRequestModal;
